import { Button } from "@/components/ui/button";
import { ArrowRight, Globe, MessageCircle, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePlatformStats } from "@/hooks/usePlatformStats";

const Hero = () => {
  const { t, i18n } = useTranslation();
  const { stats } = usePlatformStats();
  const fmt = (n: number | undefined) => (n === undefined ? "—" : n.toLocaleString(i18n.language));
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      <div className="container mx-auto px-4 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
          {/* Left — Text */}
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6 animate-slide-up">
              {t("home.hero.title")}{" "}
              <span className="text-primary">{t("home.hero.titleHighlight")}</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mb-10 animate-slide-up" style={{ animationDelay: "0.1s" }}>
              {t("home.hero.subtitleLine1")}
              <br />
              {t("home.hero.subtitleLine2")}
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4 mb-12 animate-slide-up" style={{ animationDelay: "0.2s" }}>
              <Button variant="default" size="lg" asChild>
                <Link to="/signup">
                  {t("home.hero.findTutorCta")}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/signin">
                  {t("home.hero.tutorCta")}
                </Link>
              </Button>
            </div>

            {/* Stats — hidden as a unit until the platform has credible
                two-sided traction (both display floors met); see PlatformStats.
                Avoids surfacing weak pre-launch numbers. */}
            {stats?.headlineVisible && (
            <div className="flex flex-wrap items-center gap-8 animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                  <Users className="w-5 h-5 text-foreground" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{fmt(stats?.activeStudents)}</p>
                  <p className="text-xs text-muted-foreground">{t("home.hero.statActiveStudents")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-foreground" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{fmt(stats?.expertTutors)}</p>
                  <p className="text-xs text-muted-foreground">{t("home.hero.statExpertTutors")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                  <Globe className="w-5 h-5 text-foreground" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{fmt(stats?.languages)}</p>
                  <p className="text-xs text-muted-foreground">{t("home.hero.statLanguages")}</p>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Right — Hero Image */}
          <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <div className="aspect-4/3 rounded-2xl overflow-hidden">
              <img src="/images/homepage/hero.webp" alt={t("home.hero.imageAlt")} className="w-full h-full object-cover" />
            </div>
            {/* Floating accent card — only shown when lessons are genuinely
                in progress; hidden (rather than asserting "0 live") otherwise. */}
            {stats && stats.liveLessonsNow > 0 && (
              <div className="absolute -bottom-4 -left-4 bg-card border border-border rounded-xl p-3 shadow-lg animate-slide-up" style={{ animationDelay: "0.4s" }}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-success" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground">{t("home.hero.liveSession")}</p>
                    <p className="text-[10px] text-muted-foreground">{t("home.hero.liveLessonsCount", { count: stats.liveLessonsNow.toLocaleString(i18n.language) })}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
